'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Heart } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { InfiniteScrollTrigger } from './infinite-scroll-trigger';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';
import { cn } from '../utils/cn';

export type HomeFeedFilter = 'all' | 'new' | 'liked';

export interface HomeFeedTag {
  id: string;
  name: string;
}

export interface HomeFeedItem {
  id: string;
  resultUrl?: string | null;
  thumbnailUrl?: string | null;
  resultType?: 'IMAGE' | 'VIDEO' | 'AUDIO' | null;
  prompt?: string | null;
  likesCount?: number | null;
  resultWidth?: number | null;
  resultHeight?: number | null;
}

interface HomeFeedTabContentProps {
  filter: HomeFeedFilter;
  activeTag: string | null;
  feedTags?: HomeFeedTag[];
  items: HomeFeedItem[];
  isLoading?: boolean;
  isPlaceholderData?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFilterChange: (filter: HomeFeedFilter) => void;
  onTagToggle: (tagId: string | null) => void;
  onLike: (id: string) => void;
  onLoadMore: () => void;
}

const SKELETON_HEIGHTS_LEFT = [270, 200, 160, 230, 180];
const SKELETON_HEIGHTS_RIGHT = [180, 250, 190, 210, 170];

export function HomeFeedTabContent({
  filter,
  activeTag,
  feedTags = [],
  items,
  isLoading = false,
  isPlaceholderData = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFilterChange,
  onTagToggle,
  onLike,
  onLoadMore,
}: HomeFeedTabContentProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-4">
        <FeedSortDropdown value={filter} onChange={onFilterChange} />
        {feedTags.length > 0 && (
          <div className="relative flex gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => onTagToggle(null)}
              className={cn(
                'h-[37px] flex-shrink-0 whitespace-nowrap rounded-[20px] px-4 text-xs font-semibold tracking-tight',
                activeTag === null ? 'bg-foreground text-background' : 'text-foreground',
              )}
            >
              Все
            </button>
            {feedTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => onTagToggle(tag.id)}
                className={cn(
                  'h-[37px] flex-shrink-0 whitespace-nowrap rounded-[20px] px-4 text-xs font-semibold tracking-tight',
                  activeTag === tag.id ? 'bg-foreground text-background' : 'text-foreground',
                )}
              >
                {tag.name}
              </button>
            ))}
            {feedTags.length > 3 && (
              <div className="pointer-events-none sticky bottom-0 right-0 top-0 w-7 flex-shrink-0 bg-gradient-to-l from-background to-transparent" />
            )}
          </div>
        )}
      </div>

      {isLoading || (isPlaceholderData && items.length === 0) ? (
        <FeedMasonrySkeleton />
      ) : items.length > 0 ? (
        <>
          <FeedMasonry items={items} onLike={onLike} />
          <InfiniteScrollTrigger
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">Здесь пока ничего нет</p>
        </div>
      )}
    </div>
  );
}

function FeedMasonrySkeleton() {
  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">
        {SKELETON_HEIGHTS_LEFT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {SKELETON_HEIGHTS_RIGHT.map((height, index) => (
          <div key={index} className="animate-pulse rounded-sm bg-card" style={{ height }} />
        ))}
      </div>
    </div>
  );
}

function FeedMasonry({
  items,
  onLike,
}: {
  items: HomeFeedItem[];
  onLike: (id: string) => void;
}) {
  const [left, right] = balanceMasonryColumns(items);
  const basePath = useBasePath();

  const renderItem = (item: HomeFeedItem) => (
    <Link
      key={item.id}
      href={`${basePath}/history/${item.id}?source=feed`}
      className="relative block overflow-hidden bg-card"
      style={
        item.resultWidth && item.resultHeight
          ? { aspectRatio: `${item.resultWidth}/${item.resultHeight}` }
          : { minHeight: 120 }
      }
    >
      {item.resultUrl &&
        (item.resultType === 'VIDEO' ? (
          <VideoThumbnail
            src={item.resultUrl}
            poster={item.thumbnailUrl}
            alt={item.prompt || 'Video'}
            iconSize="lg"
          />
        ) : (
          <S3Image
            src={item.thumbnailUrl || item.resultUrl}
            alt={item.prompt || 'Generated'}
            width={390}
            height={0}
            className="h-auto w-full"
            style={{ height: 'auto' }}
          />
        ))}
      <div className="absolute bottom-0 left-0 h-[69px] w-full bg-gradient-to-t from-black/[0.87] to-transparent opacity-70" />
      <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1 p-2.5">
        {(item.likesCount || 0) > 0 && (
          <button
            onClick={(event) => {
              event.preventDefault();
              onLike(item.id);
            }}
            className="flex items-center gap-1"
          >
            <Heart className="h-4 w-4 fill-red-500 text-red-500" />
            <span className="text-sm text-white">{item.likesCount}</span>
          </button>
        )}
      </div>
    </Link>
  );

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-col gap-1">{left.map(renderItem)}</div>
      <div className="flex flex-1 flex-col gap-1">{right.map(renderItem)}</div>
    </div>
  );
}

function FeedSortDropdown({
  value,
  onChange,
}: {
  value: HomeFeedFilter;
  onChange: (value: HomeFeedFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = [
    { value: 'all' as const, label: 'Популярное' },
    { value: 'new' as const, label: 'Новые' },
    { value: 'liked' as const, label: 'Мои лайки' },
  ];

  const current = options.find((option) => option.value === value)!;

  useEffect(() => {
    if (!open) return;

    const handle = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((value) => !value)}
        className="flex h-[37px] items-center gap-1.5 rounded-[20px] border border-foreground px-4 text-xs font-semibold tracking-tight text-foreground"
      >
        {current.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          className={cn('transition-transform', open && 'rotate-180')}
        >
          <path
            d="M2.5 3.75L5 6.25L7.5 3.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[120px] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={cn(
                'w-full px-4 py-2.5 text-left text-xs',
                option.value === value
                  ? 'bg-muted font-medium text-foreground'
                  : 'text-muted-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function balanceMasonryColumns<
  T extends { resultWidth?: number | null; resultHeight?: number | null },
>(items: T[]): [T[], T[]] {
  const left: T[] = [];
  const right: T[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  for (const item of items) {
    const ratio =
      item.resultWidth && item.resultHeight ? item.resultHeight / item.resultWidth : 1;
    if (leftHeight <= rightHeight) {
      left.push(item);
      leftHeight += ratio;
    } else {
      right.push(item);
      rightHeight += ratio;
    }
  }

  return [left, right];
}
